import React, { createContext, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { id as fulfillmentTypeId } from '../../../filters/options/fulfillment-type';
import { fulfillmentTypeOptions } from '../../../filters/options/fulfillment-type/options';
import filterViews from '../filter-views';
import { useIsOnReturnsAdvancedSearchView, useIsOnBatchesAdvancedSearchView, useIsOnFulfillmentsAdvancedSearchView } from '../../hooks';
import shipmentsView from '../filter-views/shipments';
import batchesView from '../filter-views/batches';
import returnsView from '../filter-views/returns';
import fulfillmentsView from '../filter-views/fulfillments';

const AdvancedSearchShipmentContext = createContext();

const AdvancedSearchShipmentContextProvider = ({ children }) => {
  const returnsInitialFilters = useIsOnReturnsAdvancedSearchView() ? returnsView.initialFilters : null;
  const batchesInitialFilters = useIsOnBatchesAdvancedSearchView() ? batchesView.initialFilters : null;
  const fulfillmentsInitialFilters = useIsOnFulfillmentsAdvancedSearchView() ? fulfillmentsView.initialFilters : null;
  const initialState = batchesInitialFilters || returnsInitialFilters || fulfillmentsInitialFilters || shipmentsView.initialFilters;
  const [filters, setFilters] = useState(initialState);

  const updateFilter = useCallback(
    (id, value, operator) => {
      const newFilter = { id, value, operator };
      const fulfillmentType = fulfillmentTypeOptions.find((type) => type.apiKey === value);
      const filterView = filterViews[fulfillmentType?.id];
      if (id === fulfillmentTypeId && filterView) {
        setFilters(filterView.initialFilters);
        return;
      }

      setFilters((prevFilters) => {
        const currentFilterIndex = prevFilters.findIndex((filter) => filter.id === id);
        return currentFilterIndex !== -1
          ? [...prevFilters.slice(0, currentFilterIndex), newFilter, ...prevFilters.slice(currentFilterIndex + 1)]
          : [...prevFilters, newFilter];
      });
    },
    [setFilters],
  );

  const clearFilter = useCallback(
    (id) => {
      setFilters((prevFilters) => {
        const currentFilterIndex = prevFilters.findIndex((filter) => filter.id === id);
        if (currentFilterIndex === -1) {
          return prevFilters;
        }
        return [...prevFilters.slice(0, currentFilterIndex), ...prevFilters.slice(currentFilterIndex + 1)];
      });
    },
    [setFilters],
  );

  const resetFilters = useCallback(() => {
    // TODO May need to maintain the current shipmentType.
    setFilters(shipmentsView.initialFilters);
  }, [setFilters]);

  const value = useMemo(
    () => ({
      filters,
      updateFilter,
      resetFilters,
      clearFilter,
    }),
    [filters, updateFilter, resetFilters, clearFilter],
  );

  return <AdvancedSearchShipmentContext.Provider value={value}>{children}</AdvancedSearchShipmentContext.Provider>;
};

AdvancedSearchShipmentContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export { AdvancedSearchShipmentContext, AdvancedSearchShipmentContextProvider };
